/*
 * Profile — display name + photo. Lives at /app/profile.
 *
 * Surfaces the existing /v1/me PATCH and /v1/me/avatar PUT endpoints in
 * a single page so users can upload a photo and tweak their display
 * name without leaving the web app. The PlayerAvatar component then
 * picks it up everywhere players are listed.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '@/lib/api';
import { AppShell } from '@/components/AppShell';
import { ImageCropUpload } from '@/components/ImageCropUpload';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import type { MeResponse } from '@golf-app/shared';

export default function Profile() {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  // currentPassword is only sent (and only required) when the email is
  // changing. The backend requires the reconfirm so a stolen 1-hour JWT
  // can't be used to swap the email and then forgot-password the account.
  const [currentPassword, setCurrentPassword] = useState('');
  // Handicap is a controlled string (not number) so users can type "." or
  // backspace through it without React clobbering the cursor. We parse to a
  // number only when saving.
  const [handicapStr, setHandicapStr] = useState('');
  // pendingAvatar holds a fresh data URL produced by the cropper before
  // the user clicks Save. avatarUrl mirrors the saved value from /me.
  const [avatarUrl, setAvatarUrl] = useState('');
  const [pendingAvatar, setPendingAvatar] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.me();
      setMe(data);
      setDisplayName(data.displayName ?? '');
      setUsername(data.username ?? '');
      setEmail(data.email ?? '');
      setHandicapStr(data.handicapIndex != null ? String(data.handicapIndex) : '');
      setAvatarUrl(data.avatarUrl ?? '');
    } catch (e: any) {
      setError(e?.message ?? "Couldn't load profile.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!me) return;
    setError(null);
    setSuccess(null);

    const trimmedName = displayName.trim();
    const trimmedUsername = username.trim().toLowerCase();
    const trimmedEmail = email.trim().toLowerCase();
    if (trimmedName.length < 1) { setError('Display name is required.'); return; }
    if (trimmedUsername.length < 3 || trimmedUsername.length > 20) {
      setError('Username must be 3 to 20 characters.');
      return;
    }
    if (!/^[a-z0-9_.]+$/.test(trimmedUsername)) {
      setError('Username can only use lowercase letters, numbers, underscores, and periods.');
      return;
    }
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Parse handicap: empty string means "no handicap" (null), otherwise
    // it must be a number between -10 and 54. We use one decimal place
    // since that's the USGA / WHS standard. Plus handicaps (better than
    // scratch) are stored as negative numbers; "+2.5" → -2.5. Accept
    // either notation so users can type the way they read the value on
    // a scorecard.
    let handicapValue: number | null = null;
    const raw = handicapStr.trim();
    if (raw.length > 0) {
      if (!/^[+-]?(\d+(\.\d+)?|\.\d+)$/.test(raw)) {
        setError('Handicap must be a number (e.g. 12, 12.5, +2.5, .8).');
        return;
      }
      const cleaned = raw.replace(/^\+/, '-'); // "+2" → "-2" (plus handicap)
      const n = parseFloat(cleaned);
      if (!Number.isFinite(n) || n < -10 || n > 54) {
        setError('Handicap must be a number between -10.0 and 54.0.');
        return;
      }
      handicapValue = Math.round(n * 10) / 10;
    }

    // Backend requires the current password to confirm an email change.
    // Catch it client-side so we don't surface the server's 400 as a
    // confusing red error.
    const emailIsChanging = trimmedEmail !== (me.email ?? '').toLowerCase();
    if (emailIsChanging && me.hasPassword && !currentPassword) {
      setError('Enter your current password to change your email.');
      return;
    }

    setSaving(true);
    try {
      // Build a single PATCH with only the changed fields. The endpoint
      // is tolerant of unchanged fields, but we keep the payload tight so
      // it's clearer in logs and avoids needless writes.
      const updates: Record<string, any> = {};
      if (trimmedName !== (me.displayName ?? '')) updates.displayName = trimmedName;
      if (trimmedUsername !== me.username) updates.username = trimmedUsername;
      if (trimmedEmail !== (me.email ?? '')) updates.email = trimmedEmail || null;
      if (handicapValue !== (me.handicapIndex ?? null)) updates.handicapIndex = handicapValue;
      if (emailIsChanging && currentPassword) updates.currentPassword = currentPassword;

      if (Object.keys(updates).length > 0) {
        await api.updateMe(updates);
      }

      // Avatar handling. Three states:
      //   pendingAvatar (data URL) → user just cropped a new image
      //   avatarUrl === '' and me.avatarUrl is set → user clicked Remove
      //   otherwise unchanged → no-op
      if (pendingAvatar) {
        await api.uploadAvatar(pendingAvatar);
      } else if (avatarUrl === '' && me.avatarUrl) {
        await api.removeAvatar();
      }

      setPendingAvatar(null);
      setCurrentPassword('');
      setSuccess('Profile saved.');
      await load();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  }

  // Did the email field move off its saved value? Used to gate showing
  // the "confirm with current password" field below.
  const emailDirty = me ? email.trim().toLowerCase() !== (me.email ?? '').toLowerCase() : false;

  if (loading) {
    return (
      <AppShell back={{ to: '/app', label: 'Home' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }

  if (!me) {
    return (
      <AppShell back={{ to: '/app', label: 'Home' }}>
        <div className="t-empty"><div className="t-empty-title">Not signed in</div></div>
      </AppShell>
    );
  }

  // The cropper hands back a fresh data URL. Once committed we treat it
  // as the canonical avatar to display until Save runs.
  const previewAvatar = pendingAvatar ?? avatarUrl;

  return (
    <AppShell back={{ to: '/app', label: 'Home' }}>
      <section className="t-hero">
        <h1>Your Profile</h1>
        <p>Set the name and photo other players will see next to your handicap in every event.</p>
      </section>

      {error && <div className="t-error">{error}</div>}
      {success && (
        <div
          className="t-hint"
          style={{
            color: 'var(--green-bright)',
            background: 'rgba(34,197,94,0.08)',
            border: '1px solid var(--green-bright)',
            padding: 10, borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {success}
        </div>
      )}

      <form className="t-form" onSubmit={handleSubmit}>
        <div className="t-field" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <PlayerAvatar avatarUrl={previewAvatar || null} displayName={displayName || me.displayName} size={64} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName || me.displayName}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-mut)', marginTop: 2 }}>
              @{me.username}
            </div>
          </div>
        </div>

        <div className="t-field">
          <label className="t-label" htmlFor="p-display">Display name</label>
          <input
            id="p-display"
            className="t-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={40}
            autoFocus
          />
          <div className="t-hint">Shown on the leaderboard and to your group.</div>
        </div>

        <div className="t-field">
          <label className="t-label" htmlFor="p-username">Username</label>
          <input
            id="p-username"
            className="t-input"
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            maxLength={20}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <div className="t-hint">3 to 20 characters. Lowercase letters, numbers, underscores, and periods. Your friends can find you by username.</div>
        </div>

        <div className="t-field">
          <label className="t-label" htmlFor="p-email">Email</label>
          <input
            id="p-email"
            className="t-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={120}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <div className="t-hint">Used for sign-in and account recovery.</div>
        </div>

        {emailDirty && me.hasPassword && (
          <div className="t-field">
            <label className="t-label" htmlFor="p-current-password">Current password</label>
            <input
              id="p-current-password"
              className="t-input"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="Enter your password to confirm the email change"
            />
            <div className="t-hint">
              Changing your email requires your current password. We also email the previous address so you'll know if someone else made this change.
            </div>
          </div>
        )}

        <div className="t-field">
          <label className="t-label" htmlFor="p-handicap">Handicap index</label>
          <input
            id="p-handicap"
            className="t-input"
            type="text"
            inputMode="decimal"
            value={handicapStr}
            onChange={(e) => setHandicapStr(e.target.value)}
            placeholder="e.g. 12.4"
            maxLength={6}
          />
          <div className="t-hint">
            Your USGA / WHS handicap index (typically 0 to 54, can be negative for plus handicaps).
            When you join an event, your current handicap is locked in for that event.
            Captains can adjust your event handicap if needed.
            Updates here only affect future events. Past rounds use the handicap that was in place when each round started.
          </div>
        </div>

        <div className="t-field">
          <div className="t-label">Profile photo</div>
          <ImageCropUpload
            value={pendingAvatar ?? avatarUrl}
            onChange={(v) => {
              if (v) {
                setPendingAvatar(v);
              } else {
                // Clear: drop the pending crop AND mark the saved one for removal.
                setPendingAvatar(null);
                setAvatarUrl('');
              }
            }}
            emptyLabel="Upload photo"
            alt="Profile photo"
          />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            className="t-btn t-btn-ghost"
            onClick={() => navigate('/app')}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="t-btn t-btn-primary t-btn-block"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </div>
      </form>
    </AppShell>
  );
}
